/**
 * TPL Price Roll — motion.dev "AnimateNumber / price switcher" port.
 *
 * On a variant change Dawn re-renders the price block with the store currency
 * (INR) and the Bucks converter then rewrites the money span into the visitor's
 * currency. Rolling on the first rewrite would roll to the wrong number, so:
 *   1. the moment the price block mutates (armed around a variant change) the
 *      OLD price is painted over it and the real element is hidden;
 *   2. we wait until the new money span carries Bucks' "converted" mark (or
 *      1.5 s pass, or the page has no Bucks at all, in which case 160 ms quiet);
 *   3. the cover comes off and every glyph rolls old → final in one motion;
 *   4. after the roll the element is plain text again so nothing else on the
 *      page (Bucks, Dawn) has to deal with our spans.
 */
(function () {
  'use strict';

  var EXPO = 'cubic-bezier(0.16, 1, 0.3, 1)';
  var ARM_MS = 2500;
  var QUIET_MS = 160;
  var BUCKS_WAIT_MS = 1500;
  var ROLL_MS = 460;
  var STAGGER_MS = 12;
  var BUCKS_MARK = '.buckscc-converted, [bucks-current]';

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }
  function supported() {
    return typeof Element !== 'undefined' && typeof Element.prototype.animate === 'function';
  }
  function bucksActive() {
    return !!document.querySelector(BUCKS_MARK);
  }

  /* Innermost element that owns the money text. */
  function leaf(el) {
    var guard = 0;
    while (el && guard++ < 6) {
      var ownText = false;
      for (var i = 0; i < el.childNodes.length; i++) {
        var nd = el.childNodes[i];
        if (nd.nodeType === 3 && nd.nodeValue.trim()) { ownText = true; break; }
      }
      if (ownText || el.children.length !== 1) return el;
      el = el.children[0];
    }
    return el;
  }

  function visiblePriceEl(container) {
    var els = container.querySelectorAll('.price-item--regular, .price-item--sale');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.tagName === 'S') continue;
      if (el.offsetParent !== null) return leaf(el);
    }
    return els[0] ? leaf(els[0]) : null;
  }

  /* Positions that differ, aligned from the right. Pure. */
  function changedPositions(oldStr, newStr) {
    var out = [];
    var o = oldStr.split(''), n = newStr.split('');
    for (var i = 0; i < n.length; i++) {
      var oi = o.length - n.length + i;
      if (oi < 0 || o[oi] !== n[i]) out.push(i);
    }
    return out;
  }

  /* Odometer: every glyph rolls; the outgoing one rides up and fades while the
     incoming one rises into place. Restores plain text when done. */
  function rollTo(el, oldText, done) {
    var text = (el.textContent || '').trim();
    var before = (oldText || '').trim();
    if (!text || text === before || !supported() || reducedMotion() || text.length > 16) { if (done) done(); return false; }
    if (el.children.length) { if (done) done(); return false; }
    var o = before.split(''), n = text.split('');
    el.textContent = '';
    var last = null;
    n.forEach(function (ch, i) {
      var slot = document.createElement('span');
      slot.className = 'tpl-pr__c';
      var incoming = document.createElement('span');
      incoming.className = 'tpl-pr__in';
      incoming.textContent = ch;
      slot.appendChild(incoming);
      var oi = o.length - n.length + i;
      var prev = oi >= 0 ? o[oi] : '';
      if (prev) {
        var ghost = document.createElement('span');
        ghost.className = 'tpl-pr__ghost';
        ghost.setAttribute('aria-hidden', 'true');
        ghost.textContent = prev;
        slot.appendChild(ghost);
        ghost.animate([{ transform: 'translateY(0)', opacity: 1 }, { transform: 'translateY(-0.75em)', opacity: 0 }], { duration: ROLL_MS - 60, delay: i * STAGGER_MS, easing: EXPO, fill: 'forwards' });
      }
      last = incoming.animate([{ transform: 'translateY(0.75em)', opacity: 0 }, { transform: 'translateY(0)', opacity: 1 }], { duration: ROLL_MS, delay: i * STAGGER_MS, easing: EXPO, fill: 'both' });
      el.appendChild(slot);
    });
    var finished = false;
    var finish = function () {
      if (finished) return;
      finished = true;
      if (el.querySelector('.tpl-pr__c')) el.textContent = text;   // back to plain text
      if (done) done();
    };
    if (last) last.onfinish = finish;
    setTimeout(finish, ROLL_MS + n.length * STAGGER_MS + 80);
    return true;
  }

  function init(container) {
    if (container.dataset.tplPriceRoll === '1') return;
    container.dataset.tplPriceRoll = '1';
    var armedUntil = 0, timer = null, animating = false, cover = null, coverTimer = null;
    var lastText = (function () { var el = visiblePriceEl(container); return el ? el.textContent.trim() : ''; })();

    function isOwn(node) {
      return !!(node && node.nodeType === 1 && (node.classList.contains('tpl-pr__cover') || node.classList.contains('tpl-pr__c') || node.classList.contains('tpl-pr__in') || node.classList.contains('tpl-pr__ghost')));
    }
    function ownRecords(records) {
      for (var i = 0; i < records.length; i++) {
        var r = records[i];
        var nodes = Array.prototype.slice.call(r.addedNodes).concat(Array.prototype.slice.call(r.removedNodes));
        if (!nodes.length) { if (!(isOwn(r.target) || isOwn(r.target.parentNode))) return false; continue; }
        for (var j = 0; j < nodes.length; j++) if (!isOwn(nodes[j]) && !(nodes[j].nodeType === 3 && isOwn(nodes[j].parentNode))) return false;
      }
      return true;
    }

    /* Paint the old price over the block while it re-renders. */
    function showCover(el) {
      if (cover || !supported() || reducedMotion() || !lastText) return;
      var host = container;
      if (window.getComputedStyle(host).position === 'static') host.style.position = 'relative';
      var r = el.getBoundingClientRect(), hr = host.getBoundingClientRect(), cs = window.getComputedStyle(el);
      cover = document.createElement('span');
      cover.className = 'tpl-pr__cover';
      cover.setAttribute('aria-hidden', 'true');
      cover.textContent = lastText;
      cover.style.cssText = 'position:absolute;left:' + (r.left - hr.left).toFixed(1) + 'px;top:' + (r.top - hr.top).toFixed(1) + 'px;' +
        'pointer-events:none;white-space:nowrap;visibility:visible;' +
        'font-family:' + cs.fontFamily + ';font-size:' + cs.fontSize + ';font-weight:' + cs.fontWeight + ';letter-spacing:' + cs.letterSpacing + ';line-height:' + cs.lineHeight + ';color:' + cs.color + ';';
      host.appendChild(cover);
      container.classList.add('tpl-pr-covering');
      coverTimer = setTimeout(settle, BUCKS_WAIT_MS);
    }
    function hideCover() {
      if (coverTimer) { clearTimeout(coverTimer); coverTimer = null; }
      if (cover) { cover.remove(); cover = null; }
      container.classList.remove('tpl-pr-covering');
    }

    function settle() {
      if (timer) { clearTimeout(timer); timer = null; }
      hideCover();
      var el = visiblePriceEl(container);
      if (!el) return;
      var now = el.textContent.trim();
      if (now === lastText) return;
      animating = true;
      var from = lastText;
      lastText = now;
      /* release one tick later: the plain-text restore inside rollTo lands as a
         mutation microtask and must still see animating = true */
      rollTo(el, from, function () { setTimeout(function () { animating = false; }, 0); });
    }

    /* The new money span is final when Bucks has marked it, or the page has no Bucks. */
    function looksFinal(el) {
      if (!bucksActive()) return true;
      return !!(el && (el.matches(BUCKS_MARK) || (el.parentElement && el.parentElement.matches(BUCKS_MARK))));
    }

    new MutationObserver(function (records) {
      if (animating) return;
      if (ownRecords(records)) return;
      var el = visiblePriceEl(container);
      if (!el) return;
      if (Date.now() > armedUntil) { lastText = el.textContent.trim(); return; }
      /* nothing to do when the block re-rendered to the same final price */
      if (!cover && el.textContent.trim() === lastText && looksFinal(el)) return;
      if (!cover) showCover(el);
      if (timer) clearTimeout(timer);
      if (looksFinal(el)) timer = setTimeout(settle, QUIET_MS);
      /* otherwise keep waiting for Bucks; the cover's own timer is the hard stop */
    }).observe(container, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['class', 'bucks-current'] });

    function arm() { armedUntil = Date.now() + ARM_MS; }
    var events = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null;
    if (typeof window.subscribe === 'function' && events && events.variantChange) window.subscribe(events.variantChange, arm);
    ['variant:change', 'change'].forEach(function (n) {
      document.addEventListener(n, function (e) {
        if (n === 'change' && !(e.target && e.target.closest && e.target.closest('variant-selects, .product-form__input'))) return;
        arm();
      }, true);
    });
  }

  function boot() { document.querySelectorAll('[id^="price-"]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();

  window.TPLPriceRoll = { changedPositions: changedPositions, rollTo: rollTo, init: init, leaf: leaf };
})();
